/* eslint-disable global-require, react/jsx-filename-extension */

import React from 'react';
import { render } from 'react-dom';
import { Router } from 'react-router';
import { ApolloProvider } from 'react-apollo';
import { HelmetProvider } from 'react-helmet-async';
import { CookiesProvider } from 'react-cookie-current';
import { Client as Styletron } from 'styletron-engine-atomic';
import { Provider as StyletronProvider } from 'styletron-react';
import { BaseProvider } from 'baseui';
import Cognito from '@letsdothis/cognito';

import Cookies from 'universal-cookie';
import { AuthServiceProvider } from '@/contexts/authService';
import { ProfileProvider } from '@/contexts/profile';
import { initAuth } from '@services/auth/cognitoAsync';
import WebVitalsTracker from '@/shared/WebVitalsTracker';
import { loadableReady } from '@loadable/component';
import {
  IntercomProvider,
  update as intercomUpdate,
} from '@/state/services/intercom';

import { LdtTheme } from './themes';
import { unloadHandler } from './state/services/tracking';
import gqlClientFactory from './state/services/gql';
import history from './routes/routes/helpers/history';
import AppContextProvider from './appContext/provider';
import LocationProvider from './appContext/LocationProvider';

const rootElement = document.getElementById('app');
const authorizeCallbackUrl = `${WEB_ROOT}${COGNITO_CALLBACK_PATH}`;

// Configure gqlClient, sessionStorage, unloadHandler
const setup = () => {
  const styletronEngine = new Styletron({
    hydrate: document.getElementsByClassName('__STYLETRON__'),
  });

  // window event handlers
  window.addEventListener('unload', unloadHandler, {
    once: true,
  });
  const cognitoClient = new Cognito.Client({
    authorizeCallbackUrl,
    cognitoBaseUrl: COGNITO_BASE_URL,
    cognitoClientId: COGNITO_CLIENT_ID,
    graphQLRoot: GQL_ROOT,
  });
  const universalCookieObject = new Cookies();
  cognitoClient.setCookies(universalCookieObject);

  return {
    styletronEngine,
    cognitoClient,
    universalCookieObject,
  };
};
const { styletronEngine, cognitoClient, universalCookieObject } = setup();

const renderApp = async () => {
  // Require routes within block scope (see hot reloading below)
  const routes = require('./routes').default;
  const authService = await initAuth(cognitoClient, universalCookieObject);
  const gqlClient = gqlClientFactory(authService);
  window.UNSAFE_DO_NOT_USE_EVER_GQL_CLIENT = gqlClient;

  // Client application uses Router component, browser history and many more fun things
  const App = () => {
    return (
      <AuthServiceProvider authService={authService}>
        <CookiesProvider>
          <AppContextProvider>
            <WebVitalsTracker>
              <HelmetProvider>
                <ApolloProvider client={gqlClient}>
                  <StyletronProvider value={styletronEngine}>
                    <BaseProvider theme={LdtTheme}>
                      <LocationProvider>
                        <ProfileProvider>
                          <IntercomProvider>
                            <Router
                              history={history}
                              routes={routes(authService)}
                              onUpdate={() => {
                                intercomUpdate();
                              }}
                            />
                          </IntercomProvider>
                        </ProfileProvider>
                      </LocationProvider>
                    </BaseProvider>
                  </StyletronProvider>
                </ApolloProvider>
              </HelmetProvider>
            </WebVitalsTracker>
          </AppContextProvider>
        </CookiesProvider>
      </AuthServiceProvider>
    );
  };

  loadableReady(() => {
    render(<App />, rootElement);
  });
};

// Support hot reloading of components
if (module.hot) {
  const RedBox = require('redbox-react');

  // Use routes as dependency entry
  module.hot.accept('./routes', async () => {
    try {
      await renderApp();
    } catch (error) {
      // Show overlay for runtime errors
      render(<RedBox error={error} />, rootElement);
    }
  });
}

renderApp();
